import { ErrorInfo } from "@timeedit/registration-components";
import { ITEToken } from "@timeedit/registration-schemas";
import { translations } from "@timeedit/registration-shared";
import { EAppId } from "@timeedit/types/lib/enums";
import { EAppEnv } from "@timeedit/types/lib/enums/appEnv.enum";
import { ITETokenInfo } from "@timeedit/types/lib/types";
import { AuthWrapper, ThemeWrapper } from "@timeedit/ui-components";
import { useState } from "react";
import { useLazyFetchRegistrationQuery } from "../../redux/api/registration";
import { useLazyFetchTeOrganizationQuery } from "../../redux/api/te";
import { useAppDispatch, useLazyUserData } from "../../redux/hooks";
import { setLoginData } from "../../redux/slices/user";
import { seedHubCache } from "./seedHubCache";

export const urls: Pick<ITETokenInfo, "AUTH_URL"> = {
  AUTH_URL: import.meta.env.SSO_AUTH_DOMAIN,
};

interface AuthRootProps extends React.PropsWithChildren {
  isHub?: boolean;
}

export function AuthRoot({
  children,
  isHub = false,
}: AuthRootProps): React.JSX.Element {
  const dispatch = useAppDispatch();

  // In hub mode, gate children rendering until `onLoginSuccess` has seeded the
  // cache. Without this gate, downstream `useFetchTeUserQuery` consumers would
  // subscribe to an empty cache the moment SSO completes and fire the very
  // request we're trying to eliminate.
  const [isReady, setIsReady] = useState(!isHub);

  const [triggerFetchOrganization, org] = useLazyFetchTeOrganizationQuery();

  const [triggerFetchUserData, user] = useLazyUserData();

  const [triggerFetchRegistration] = useLazyFetchRegistrationQuery();

  if (org.isError || user.isError) {
    return (
      <ThemeWrapper scope="scope-registration" defaultComponentSize="middle">
        <ErrorInfo
          semanticTitleLevel={1}
          error={org.isError ? org.error : user.error}
          recover={onLogout}
          translations={{
            retry: translations.retry,
            title: translations.errorInRegistration,
          }}
        />
      </ThemeWrapper>
    );
  }

  return (
    <AuthWrapper
      // appId decides which SSO login flow the deep-link redirects to, so it
      // must match the host: the Hub when embedded, Registration standalone.
      appId={isHub ? EAppId.TE_HUB : EAppId.TE_REGISTRATION}
      authUrl={urls.AUTH_URL}
      renderChildrenWhileLoading={false}
      onLoginSuccess={onLoginSuccess}
      onLogout={onLogout}
      customerSignature={org.currentData?.customerSignature}
      enableLogs={import.meta.env.VITE_TE_APP_ENV === EAppEnv.DEVELOPMENT}
    >
      {isReady ? children : null}
    </AuthWrapper>
  );

  async function onLoginSuccess(token: ITEToken): Promise<void> {
    dispatch(setLoginData(token));

    if (isHub) {
      await seedHubCache(dispatch, token);
      setIsReady(true);
    }

    await Promise.all([
      triggerFetchUserData({ token, preferCacheValue: true })?.unwrap(),
      triggerFetchOrganization(
        { organizationId: token.organizationId },
        true
      ).unwrap(),
    ]);

    triggerFetchRegistration();
  }

  function onLogout(): void {
    dispatch(setLoginData(undefined));
  }
}
